import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const encoderDecoderBlockComponent: ComponentDef = {
  id: 'encoder-decoder-block',
  name: 'Encoder-Decoder Block',
  description: 'T5/BART-style full encoder-decoder Transformer block: encoder self-attention, decoder self-attention (causal), cross-attention, and FFN with residual connections.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'enc_len', label: 'Encoder seq length', type: 'number', default: 128, min: 1, max: 4096, step: 1 },
    { name: 'dec_len', label: 'Decoder seq length', type: 'number', default: 64, min: 1, max: 2048, step: 1 },
    { name: 'd_ff', label: 'FFN dimension', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    {
      name: 'norm_type',
      label: 'Norm type',
      type: 'select',
      default: 'LayerNorm',
      options: [
        { label: 'LayerNorm', value: 'LayerNorm' },
        { label: 'RMSNorm (T5)', value: 'RMSNorm' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const enc_len = Number(params.enc_len);
    const dec_len = Number(params.dec_len);
    const d_ff = Number(params.d_ff);
    const normType = String(params.norm_type || 'LayerNorm');
    const d_head = d_model / n_heads;

    const g = new GraphBuilder();
    g.input('enc_in', [enc_len, d_model]);
    g.input('dec_in', [dec_len, d_model]);

    // ── Encoder block: bidirectional self-attention + FFN ──
    g.norm('enc_norm1', 'enc_in', normType);
    g.linear('enc_q', 'enc_norm1', [d_model, d_model]);
    g.linear('enc_k', 'enc_norm1', [d_model, d_model]);
    g.linear('enc_v', 'enc_norm1', [d_model, d_model]);
    g.reshape('enc_sq', 'enc_q', [n_heads, enc_len, d_head]);
    g.reshape('enc_sk', 'enc_k', [n_heads, enc_len, d_head]);
    g.reshape('enc_sv', 'enc_v', [n_heads, enc_len, d_head]);
    g.transpose('enc_kt', 'enc_sk', [n_heads, d_head, enc_len], 'K^T');
    g.matmul('enc_qk', 'enc_sq', 'enc_kt', [n_heads, enc_len, enc_len]);
    g.scale('enc_scale', 'enc_qk', '1/√d_k');
    g.softmax('enc_softmax', 'enc_scale', -1);
    g.matmul('enc_av', 'enc_softmax', 'enc_sv', [n_heads, enc_len, d_head]);
    g.reshape('enc_cat', 'enc_av', [enc_len, d_model]);
    g.linear('enc_wo', 'enc_cat', [d_model, d_model]);
    g.add('enc_add1', 'enc_in', 'enc_wo');
    g.norm('enc_norm2', 'enc_add1', normType);
    g.linear('enc_ff1', 'enc_norm2', [d_model, d_ff]);
    g.activation('enc_act', 'enc_ff1', 'GELU');
    g.linear('enc_ff2', 'enc_act', [d_ff, d_model]);
    g.add('enc_out', 'enc_add1', 'enc_ff2');

    // ── Decoder block: causal self-attention ──
    g.norm('dec_norm1', 'dec_in', normType);
    g.linear('dec_q', 'dec_norm1', [d_model, d_model]);
    g.linear('dec_k', 'dec_norm1', [d_model, d_model]);
    g.linear('dec_v', 'dec_norm1', [d_model, d_model]);
    g.reshape('dec_sq', 'dec_q', [n_heads, dec_len, d_head]);
    g.reshape('dec_sk', 'dec_k', [n_heads, dec_len, d_head]);
    g.reshape('dec_sv', 'dec_v', [n_heads, dec_len, d_head]);
    g.transpose('dec_kt', 'dec_sk', [n_heads, d_head, dec_len], 'K^T');
    g.matmul('dec_qk', 'dec_sq', 'dec_kt', [n_heads, dec_len, dec_len]);
    g.scale('dec_scale', 'dec_qk', '1/√d_k');
    g.mask('dec_mask', 'dec_scale');
    g.softmax('dec_softmax', 'dec_mask', -1);
    g.matmul('dec_av', 'dec_softmax', 'dec_sv', [n_heads, dec_len, d_head]);
    g.reshape('dec_cat', 'dec_av', [dec_len, d_model]);
    g.linear('dec_wo', 'dec_cat', [d_model, d_model]);
    g.add('dec_add1', 'dec_in', 'dec_wo');

    // ── Cross-attention: Q from decoder, K/V from encoder ──
    g.norm('cross_norm', 'dec_add1', normType);
    g.linear('cross_q', 'cross_norm', [d_model, d_model]);
    g.linear('cross_k', 'enc_out', [d_model, d_model]);
    g.linear('cross_v', 'enc_out', [d_model, d_model]);
    g.reshape('cross_sq', 'cross_q', [n_heads, dec_len, d_head]);
    g.reshape('cross_sk', 'cross_k', [n_heads, enc_len, d_head]);
    g.reshape('cross_sv', 'cross_v', [n_heads, enc_len, d_head]);
    g.transpose('cross_kt', 'cross_sk', [n_heads, d_head, enc_len], 'K^T');
    g.matmul('cross_qk', 'cross_sq', 'cross_kt', [n_heads, dec_len, enc_len]);
    g.scale('cross_scale', 'cross_qk', '1/√d_k');
    g.softmax('cross_softmax', 'cross_scale', -1);
    g.matmul('cross_av', 'cross_softmax', 'cross_sv', [n_heads, dec_len, d_head]);
    g.reshape('cross_cat', 'cross_av', [dec_len, d_model]);
    g.linear('cross_wo', 'cross_cat', [d_model, d_model]);
    g.add('dec_add2', 'dec_add1', 'cross_wo');

    // ── Decoder FFN ──
    g.norm('dec_norm3', 'dec_add2', normType);
    g.linear('dec_ff1', 'dec_norm3', [d_model, d_ff]);
    g.activation('dec_act', 'dec_ff1', 'GELU');
    g.linear('dec_ff2', 'dec_act', [d_ff, d_model]);
    g.add('dec_out', 'dec_add2', 'dec_ff2');

    g.output('out', 'dec_out');
    return g.build();
  }
};
